/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {SpecialOrderDetailModel} from '../../../api/ms-specialorders/contracts/special-order-detail.model';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {SpecialOrderMaterialStatusGroupModel} from '../../../api/ms-specialorders/contracts/special-order-material-status-group.model';
import {AvailableActions} from '../../../api/ms-specialorders/contracts/available-actions.enum';
import {SpecialOrderContextMenuModel} from '../../models/special-order-context-menu.model';
import {UserService} from '../../../shared/services/user.service';
import {Permission} from '../../../shared/utils/permission.enum';
import {SpecialOrderMaterialAlertsGroupModel} from '../../../api/ms-specialorders/contracts/special-order-material-alerts-group.model';
import {MaterialService} from '../../../api/ms-specialorders/material/material.service';
import {SpoActionsUtilService} from '../../services/spo-actions-util/spo-actions-util.service';
import {SpecialOrderMaterialStatus} from '../../../api/ms-specialorders/contracts/special-order-material-status.enum';

@Component({
    selector: 'app-spo-detail-page-header',
    templateUrl: './spo-detail-page-header.component.html',
    styleUrls: ['./spo-detail-page-header.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpoDetailPageHeaderComponent implements OnInit, OnDestroy {

    @Input() spoDetails: SpecialOrderDetailModel;
    @Output() contextMenuSend = new EventEmitter();
    @Output() contextMenuEdit = new EventEmitter();
    @Output() contextMenuDelete = new EventEmitter();
    public contextMenuOptions: SpecialOrderContextMenuModel;
    public availableActions = AvailableActions;
    public isEditPage: boolean;
    public Permission = Permission;
    // Key has to be a string, since it's defined as a string on the API contract... :(
    public statusRetryActions = new Map<string, Function>();
    // Temporary variables while status aren't implemented
    public materialStatus: SpecialOrderMaterialStatusGroupModel[] = [];
    public materialAlerts: SpecialOrderMaterialStatusGroupModel[] = [
        {status: {code: 'DEFAULT', description: 'Alerts', active: true}, numberOfMaterials: 0}
    ];
    private subscriptions = new Subscription();

    // TODO: Must be updated to receive a list of buttons and actions for the menu

    constructor(private activatedRoute: ActivatedRoute,
                private materialService: MaterialService,
                public userService: UserService,
                public spoActionsUtilService: SpoActionsUtilService,
                private router: Router) {
    }

    ngOnInit(): void {
        // FIXME: this should be an input parameter and not interpreted from the URL...
        this.subscriptions.add(this.activatedRoute.url.subscribe((segment) => {
            this.isEditPage = segment[0] && segment[0].path === 'edit';
        }));

        this.subscriptions.add(this.materialService.getAllMaterialStatus().subscribe(data => {
            this.materialStatus = data.map(status => ({
                status,
                numberOfMaterials: 0
            }));
            this.updateMaterialsStatusGroups();
        }));

        this.updateMaterialAlerts();
        this.enableContextMenuOptions();
        this.enableDraftRetry();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    clickContextMenuEdit(): void {
        this.contextMenuEdit.emit();
    }

    clickContextMenuDelete(): void {
        this.contextMenuDelete.emit();
    }

    clickContextMenuSend(): void {
        if (this.contextMenuOptions.canSubmit || this.contextMenuOptions.canRetry) {
            this.contextMenuSend.emit();
        }
    }

    enableContextMenuOptions(): void {
        this.contextMenuOptions = {
            canEdit: false,
            canDelete: false,
            canCreate: false,
            canSubmit: false,
            canRetry: false
        };
        if (this.spoDetails.availableActions != null) {
            this.contextMenuOptions.canCreate = this.spoDetails.availableActions.includes(AvailableActions.CREATE);
            this.contextMenuOptions.canEdit = this.spoDetails.availableActions.includes(AvailableActions.EDIT)
                    || this.spoDetails.availableActions.includes(AvailableActions.EDIT_POSITION_DELETE)
                    || this.spoDetails.availableActions.includes(AvailableActions.EDIT_POSITION_CANCEL);
            this.contextMenuOptions.canDelete = this.spoDetails.availableActions.includes(AvailableActions.DELETE);
            this.contextMenuOptions.canSubmit = this.spoDetails.availableActions.includes(AvailableActions.SUBMIT);
            this.contextMenuOptions.canRetry = this.spoDetails.availableActions.includes(AvailableActions.RESUBMIT);
        }
    }

    disableStatusGroup(): boolean {
        return this.isEditPage;
    }

    disableContextMenuSend(): boolean {
        return !this.contextMenuOptions.canSubmit && !this.contextMenuOptions.canRetry;
    }

    enableDraftRetry(): void {
        if (this.spoDetails == null || this.spoDetails.materialStatusGroups == null) {
            return;
        }

        const draft = this.spoDetails.materialStatusGroups.find(s => s.status.code === SpecialOrderMaterialStatus.DRAFT);

        if (!this.isEditPage
                && draft != null
                && draft.numberOfMaterials > 0
                && this.spoDetails.availableActions.includes(AvailableActions.RESUBMIT)) {
            this.statusRetryActions.set(SpecialOrderMaterialStatus.DRAFT, () => this.retrySubmit());
        }
    }

    retrySubmit(): void {
        const draft = this.spoDetails != null && this.spoDetails.materialStatusGroups != null
                ? this.spoDetails.materialStatusGroups.find(s => s.status.code === SpecialOrderMaterialStatus.DRAFT)
                : null;
        const draftCount = draft != null ? draft.numberOfMaterials : 0;
        this.spoActionsUtilService.submitSpecialOrder(this.spoDetails.number, draftCount, false, true,
                () => {
                    this.router.navigate(['.']);
                });
    }

    private updateMaterialsStatusGroups(): void {
        if (this.spoDetails.materialStatusGroups != null) {
            this.materialStatus = this.materialStatus.map(materialStatus => {
                const data = this.spoDetails.materialStatusGroups.find(original => original.status.code === materialStatus.status.code);
                return {...materialStatus, ...data};
            });
        }
    }

    private updateMaterialAlerts(): void {
        if (this.spoDetails.materialAlertsGroups != null) {
            this.spoDetails.materialAlertsGroups.forEach((spoAlert: SpecialOrderMaterialAlertsGroupModel) => {
                const [alertElement]: SpecialOrderMaterialStatusGroupModel[] =
                        this.materialAlerts.filter(materialAlert => materialAlert.status.code === spoAlert.type.code);
                if (alertElement != null) {
                    alertElement.numberOfMaterials = spoAlert.count;
                }
            });
        }
    }

}
